// src/hooks/useFirebaseSubmissions.js

import { getDatabase, ref, set, get, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import createZoomMeeting from '../helpers/createZoomMeeting';

const BACKEND_URL =
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:4000'
    : 'https://ai-realtor-2ktt.onrender.com');

// const SEAM_URL = (process.env.NODE_ENV === 'development'
//     ? 'http://localhost:8000'
//     : 'https://ai-realtor.onrender.com');

const EMAIL_URL = (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://ai-realtor.onrender.com');

// ─── Unchanged helpers ────────────────────────────────────────────────────────

const generateRandomCodeName = () => {
  const prefix = "Viewing-";
  const randomString = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}${randomString}`;
};

function generateRequestId() {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
}

const parseViewingTime = (displayText) => {
  console.log('[PARSE] Input displayText:', displayText);

  if (displayText.startsWith("Specific Date:")) {
    const match = displayText.match(/Specific Date: ([^,]+), (\d{4}), from ([^ ]+ [AP]M)/);
    if (!match) {
      const altMatch = displayText.match(/Specific Date: (\d{1,2} \w+ \d{4}), from (\d{1,2}:\d{2})(?: to (\d{1,2}:\d{2}))?/);
      if (altMatch) {
        const [, dateStr, startTime] = altMatch;
        return new Date(`${dateStr} ${startTime}`);
      }
      throw new Error(`Invalid specific date format: ${displayText}`);
    }
    const [, monthDay, year, time] = match;
    return new Date(`${monthDay}, ${year} ${time}`);
  }

  if (displayText.startsWith("Weekly (")) {
    const matchWithDate = displayText.match(/Weekly \((\w+)\): ([0-9:]+ [AP]M).*on (\d{4}-\d{2}-\d{2})/);
    if (matchWithDate) {
      const [, , time, dateStr] = matchWithDate;
      const [year, month, day] = dateStr.split("-");
      let [timeStr, ampm] = time.split(" ");
      let [hours, minutes] = timeStr.split(":").map(Number);
      if (ampm === "PM" && hours !== 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
      return new Date(year, month - 1, day, hours, minutes);
    }
    const match = displayText.match(/Weekly \((\w+)\): ([0-9:]+ [AP]M)/);
    if (!match) throw new Error(`Invalid weekly format: ${displayText}`);
    const [, , time] = match;
    const [timeStr, ampm] = time.split(" ");
    let [hours, minutes] = timeStr.split(":").map(Number);
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  const timeMatch = displayText.match(/(\d{1,2}:\d{2})\s*(AM|PM)/i);
  if (timeMatch) {
    const [, time, period] = timeMatch;
    const [hours, minutes] = time.split(':').map(Number);
    let hour24 = hours;
    if (period.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
    if (period.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;
    const date = new Date();
    date.setHours(hour24, minutes, 0, 0);
    return date;
  }

  throw new Error(`Unrecognized time format: ${displayText}`);
};

// ─── NEW: Write Google Calendar event (R3) ────────────────────────────────────
/**
 * Calls the backend to create a GCal event for a confirmed viewing.
 * Returns the googleCalendarEventId string, or null if GCal is not connected
 * or the call fails. Never throws — GCal is non-blocking.
 *
 * @param {object} params
 * @param {string} params.ownerId       - Agent UID (used by backend to load tokens)
 * @param {string} params.startISO      - ISO-8601 start time
 * @param {string} params.endISO        - ISO-8601 end time (defaults to start + 1h)
 * @param {string} params.address       - Property address (used as event summary)
 * @param {string} params.tenantEmail   - Invited as attendee
 * @param {string} params.tenantName    - Included in event description
 * @param {string} params.zoomJoinUrl   - Appended to event description if present
 */
const createGCalEvent = async ({
  ownerId,
  startISO,
  endISO,
  address,
  tenantEmail,
  tenantName,
  zoomJoinUrl,
}) => {
  try {
    const end = endISO || new Date(new Date(startISO).getTime() + 60 * 60 * 1000).toISOString();

    const description = [
      `Tenant: ${tenantName || tenantEmail || 'Unknown'}`,
      tenantEmail ? `Email: ${tenantEmail}` : null,
      zoomJoinUrl ? `Zoom: ${zoomJoinUrl}` : null,
      'Created automatically by Tenant Access.',
    ]
      .filter(Boolean)
      .join('\n');

    const response = await fetch(`${BACKEND_URL}/google-calendar/create-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid: ownerId,
        summary: `Viewing – ${address || 'Property'}`,
        description,
        startISO: startISO,
        endISO: end,
        attendeeEmail: tenantEmail || null,
      }),
    });

    if (!response.ok) {
      console.warn('[GCal] create-event HTTP error:', response.status);
      return null;
    }

    const data = await response.json();
    if (data.skipped) {
      console.log('[GCal] create-event skipped (GCal not connected for this agent)');
      return null;
    }

    console.log('[GCal] Event created:', data.googleCalendarEventId);
    return data.googleCalendarEventId || null;
  } catch (err) {
    // Non-fatal — booking has already succeeded at this point
    console.warn('[GCal] create-event error (non-fatal):', err.message);
    return null;
  }
};

// ─── Email helpers (unchanged from original) ──────────────────────────────────

const useFirebaseSubmissions = () => {
  const database = getDatabase();

  const getUserUID = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    return user ? user.uid : "anonymous";
  };

  const sendRenterEmail = async (viewingRequestData, listingData, zoomMeeting = null, accessCodeResult = null) => {
    try {
      const renterEmail = viewingRequestData.email;
      const renterName = viewingRequestData.name;
      if (!renterEmail) return;

      const propertyAddress = listingData?.address
        ? `${listingData.address}, ${listingData.city || ''}, ${listingData.state || ''}`.trim()
        : 'the property';

      let viewingTimeStr = 'Not specified';
      if (viewingRequestData.dateDetails) {
        if (viewingRequestData.dateDetails.displayText) {
          viewingTimeStr = viewingRequestData.dateDetails.displayText;
        } else if (viewingRequestData.dateDetails.startTime) {
          const startDate = new Date(viewingRequestData.dateDetails.startTime);
          const endDate = viewingRequestData.dateDetails.endTime
            ? new Date(viewingRequestData.dateDetails.endTime)
            : null;
          if (endDate) {
            viewingTimeStr = `Specific Date: ${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}, from ${startDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })} to ${endDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
          } else {
            viewingTimeStr = `Specific Date: ${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}, from ${startDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
          }
        }
      }

      const subject = (zoomMeeting || accessCodeResult)
        ? `Viewing Request Approved - Meeting Details Included - ${propertyAddress}`
        : `Viewing Request Confirmation - ${propertyAddress}`;

      let emailText, emailHtml;

      if (zoomMeeting || accessCodeResult) {
        let zoomInfo = '';
        let accessCodeInfo = '';
        if (zoomMeeting) {
          zoomInfo = `\nZoom Meeting Details:\n- Meeting ID: ${zoomMeeting.meetingId}\n- Join URL: ${zoomMeeting.joinUrl}\n- Start Time: ${new Date(zoomMeeting.startTime).toLocaleString()}\n\n`;
        }
        if (accessCodeResult?.accessCode) {
          accessCodeInfo = `\nAccess Code: ${accessCodeResult.accessCode}\n\nYour access code will be active 5 minutes before your scheduled viewing time and will expire 15 minutes after.\n\n`;
        }
        emailText = `Hello ${renterName || 'there'},\n\nYour viewing request has been approved.\n\nProperty: ${propertyAddress}\nScheduled Time: ${viewingTimeStr}\n\n${zoomInfo}${accessCodeInfo}Best regards,\nTenant Access`;
        emailHtml = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;"><h2 style="color: #4CAF50;">Viewing Request Approved!</h2><p>Hello ${renterName || 'there'},</p><p>Your request has been approved.</p><div style="background-color: #F8F8F8; padding: 15px; border-radius: 8px; margin: 20px 0;"><p><strong>Property:</strong> ${propertyAddress}</p><p><strong>Scheduled Time:</strong> ${viewingTimeStr}</p>${zoomMeeting ? `<div style="margin-top:15px;padding-top:15px;border-top:1px solid #ddd;"><p><strong>Zoom:</strong> <a href="${zoomMeeting.joinUrl}">${zoomMeeting.joinUrl}</a></p><p><strong>Meeting ID:</strong> ${zoomMeeting.meetingId}</p></div>` : ''}${accessCodeResult?.accessCode ? `<div style="margin-top:15px;padding-top:15px;border-top:1px solid #ddd;"><p><strong>Access Code:</strong> <span style="font-size:20px;font-weight:bold;color:#4CAF50;">${accessCodeResult.accessCode}</span></p><p style="color:#666;font-size:12px;">Active 5 min before viewing, expires 15 min after</p></div>` : ''}</div><p>Best regards,<br>Tenant Access</p></div>`;
      } else {
        emailText = `Hello ${renterName || 'there'},\n\nThank you for your viewing request!\n\nProperty: ${propertyAddress}\nScheduled Time: ${viewingTimeStr}\n\nYour request is being processed.\n\nBest regards,\nTenant Access`;
        emailHtml = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;"><h2 style="color: #4CAF50;">Viewing Request Confirmation</h2><p>Hello ${renterName || 'there'},</p><p>Thank you for your viewing request!</p><div style="background-color: #F8F8F8; padding: 15px; border-radius: 8px; margin: 20px 0;"><p><strong>Property:</strong> ${propertyAddress}</p><p><strong>Scheduled Time:</strong> ${viewingTimeStr}</p></div><p>Best regards,<br>Tenant Access</p></div>`;
      }

      const response = await fetch(`${EMAIL_URL}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: renterEmail, subject, text: emailText, html: emailHtml }),
      });
      if (!response.ok) console.error('[EMAIL] Failed to send renter email:', response.status);
      else console.log('[EMAIL] Renter email sent');
    } catch (error) {
      console.error('[EMAIL] Error sending renter email:', error.message);
    }
  };

  const sendOwnerEmail = async (viewingRequestData, listingData, ownerId) => {
    if (!ownerId) return;
    try {
      const response = await fetch(`${EMAIL_URL}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId, viewingRequestData, listingData }),
      });
      if (!response.ok) console.error('[EMAIL] Failed to send owner email:', response.status);
      else console.log('[EMAIL] Owner email sent');
    } catch (error) {
      console.error('[EMAIL] Error sending owner email:', error.message);
    }
  };

  // ─── Main submission function ────────────────────────────────────────────────

  const submitViewingRequest = async (listingId, viewingRequestData, questionnaireData) => {
    if (!listingId) throw new Error("Listing ID is required to submit the viewing request.");

    try {
      const uid = getUserUID();
      const requestId = generateRequestId();

      // Fetch listing data
      let listingData = null;
      try {
        const listingRef = ref(database, `listings/${listingId}`);
        const listingSnapshot = await get(listingRef);
        if (listingSnapshot.exists()) listingData = listingSnapshot.val();
      } catch (listingError) {
        console.error('[SUBMISSION] Error fetching listing data:', listingError);
      }

      // Build and save the booking record
      const { status: _, ...restViewingData } = viewingRequestData;
      const dataToSave = {
        ...restViewingData,
        uid,
        requestId,
        questionnaire: questionnaireData || {},
        submittedAt: Date.now(),
        status: "approved",
      };

      const viewingRequestRef = ref(database, `listings/${listingId}/viewingRequests/${requestId}`);
      await set(viewingRequestRef, dataToSave);
      console.log('[SUBMISSION] Viewing request saved');

      // ── Create Zoom meeting ────────────────────────────────────────────────
      let zoomMeeting = null;
      let viewingTime = null; // resolve once, reused by GCal + access code

      try {
        if (viewingRequestData.dateDetails) {
          if (viewingRequestData.dateDetails.startTime) {
            viewingTime = new Date(viewingRequestData.dateDetails.startTime);
          } else if (viewingRequestData.dateDetails.displayText) {
            try {
              viewingTime = parseViewingTime(viewingRequestData.dateDetails.displayText);
            } catch (e) {
              console.error('[SUBMISSION] Could not parse viewing time:', e);
            }
          }
        }

        if (viewingTime && !isNaN(viewingTime.getTime())) {
          const topic = `Viewing – ${listingData?.address || 'Property'}, ${listingData?.city || ''}`;
          const zoomResult = await createZoomMeeting({
            dateTime: viewingTime.toISOString(),
            topic,
            agentEmail: listingData?.ownerEmail || 'noreply@tenantguidedtours.com',
            tenantEmail: viewingRequestData.email,
          });

          zoomMeeting = {
            joinUrl: zoomResult.joinUrl,
            meetingId: zoomResult.meetingId,
            startTime: viewingTime.toISOString(),
          };

          await update(viewingRequestRef, { zoom: zoomMeeting });
          console.log('[SUBMISSION] Zoom meeting created and saved');
        }
      } catch (zoomError) {
        console.error('[SUBMISSION] Zoom error (non-fatal):', zoomError.message);
      }

      // ── R3: Create Google Calendar event ──────────────────────────────────
      // Runs after Zoom so we can embed the join URL in the event description.
      // Completely non-blocking — any failure is swallowed.
      try {
        const ownerId = listingData?.ownerId;
        if (ownerId && viewingTime && !isNaN(viewingTime.getTime())) {
          const durationMs =
            viewingRequestData.dateDetails?.endTime
              ? new Date(viewingRequestData.dateDetails.endTime).getTime() - viewingTime.getTime()
              : 60 * 60 * 1000; // default 1 hour

          const endISO = new Date(viewingTime.getTime() + durationMs).toISOString();

          const googleCalendarEventId = await createGCalEvent({
            ownerId,
            startISO: viewingTime.toISOString(),
            endISO,
            address: listingData?.address
              ? `${listingData.address}, ${listingData.city || ''}`
              : undefined,
            tenantEmail: viewingRequestData.email,
            tenantName: viewingRequestData.name,
            zoomJoinUrl: zoomMeeting?.joinUrl,
          });

          if (googleCalendarEventId) {
            // Persist on the booking record so it can be deleted later if needed
            await update(viewingRequestRef, { googleCalendarEventId });
            console.log('[SUBMISSION] GCal event ID stored:', googleCalendarEventId);
          }
        }
      } catch (gcalError) {
        // Never block the booking
        console.warn('[SUBMISSION] GCal write error (non-fatal):', gcalError.message);
      }

      // ── Generate access code ───────────────────────────────────────────────
      let accessCodeResult = null;
      try {
        if (viewingTime && !isNaN(viewingTime.getTime())) {
          const baseCodeName = generateRandomCodeName();
          const namePart = viewingRequestData.name?.trim() || "";
          const emailPart = viewingRequestData.email?.trim() || "";
          const finalAccessCodeName = [namePart, emailPart, baseCodeName].filter(Boolean).join("-");

          const startsAt = new Date(viewingTime.getTime());
          startsAt.setMinutes(viewingTime.getMinutes() - 5);
          const endsAt = new Date(viewingTime.getTime());
          endsAt.setMinutes(viewingTime.getMinutes() + 15);

          const pad = (n) => n.toString().padStart(2, "0");
          const formattedStartsAt = `${startsAt.getFullYear()}-${pad(startsAt.getMonth() + 1)}-${pad(startsAt.getDate())}T${pad(startsAt.getHours())}:${pad(startsAt.getMinutes())}`;
          const formattedEndsAt = `${endsAt.getFullYear()}-${pad(endsAt.getMonth() + 1)}-${pad(endsAt.getDate())}T${pad(endsAt.getHours())}:${pad(endsAt.getMinutes())}`;

          let actualPinCode = null;
          const ownerId = listingData?.ownerId || listingData?.ownerUid;
          const seamServerUrl = process.env.REACT_APP_SEAM_SERVER_URL ||
            (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://ai-realtor-seam.onrender.com');

          if (ownerId) {
            try {
              const locksResponse = await fetch(`${seamServerUrl}/api/user-locks?uid=${ownerId}`);
              if (locksResponse.ok) {
                const locksData = await locksResponse.json();
                const propertyLock = (locksData.devices || []).find(
                  (device) => device.associatedPropertyId === listingId
                );
                if (propertyLock) {
                  const codeResponse = await fetch(`${seamServerUrl}/api/access-codes/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      uid: ownerId,
                      deviceId: propertyLock.lockId,
                      name: finalAccessCodeName,
                      startsAt: startsAt.toISOString(),
                      endsAt: endsAt.toISOString(),
                      isOfflineAccessCode: propertyLock.deviceType?.toLowerCase().includes('igloohome') || false,
                    }),
                  });
                  if (codeResponse.ok) {
                    const codeData = await codeResponse.json();
                    actualPinCode = codeData.accessCode?.code || codeData.accessCode?.access_code?.code;
                  }
                }
              }
            } catch (lockError) {
              console.error('[SUBMISSION] Smart lock error:', lockError.message);
            }
          }

          if (!actualPinCode) {
            actualPinCode = Math.floor(100000 + Math.random() * 900000).toString();
          }

          const accessCodeData = {
            access_code: actualPinCode,
            email: viewingRequestData.email,
            phone: viewingRequestData.phone,
            start_time: formattedStartsAt,
            end_time: formattedEndsAt,
            address: listingData?.address
              ? `${listingData.address}, ${listingData.city || ''}, ${listingData.state || ''}`.trim()
              : 'the property',
            listingId,
            demoMode: true,
          };

          const accessCodeResponse = await fetch(`${EMAIL_URL}/schedule-access-code-alerts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(accessCodeData),
          });

          if (accessCodeResponse.ok) {
            const result = await accessCodeResponse.json();
            accessCodeResult = { accessCode: actualPinCode, ...result };
          } else {
            accessCodeResult = { accessCode: actualPinCode };
          }
        }
      } catch (accessCodeError) {
        console.error('[SUBMISSION] Access code error:', accessCodeError.message);
      }

      // ── Send emails ───────────────────────────────────────────────────────
      if (listingData && viewingRequestData.email) {
        sendRenterEmail(viewingRequestData, listingData, zoomMeeting, accessCodeResult).catch(
          (e) => console.error('[SUBMISSION] Renter email failed:', e)
        );
      }
      if (listingData?.ownerId) {
        sendOwnerEmail(viewingRequestData, listingData, listingData.ownerId).catch(
          (e) => console.error('[SUBMISSION] Owner email failed:', e)
        );
      }

      return { success: true, requestId };
    } catch (error) {
      console.error("Error submitting viewing request:", error);
      throw error;
    }
  };

  return { submitViewingRequest };
};

export default useFirebaseSubmissions;